<template>
  <div class="bz-picker">
    <!-- favorites chip row -->
    <div v-if="favEntries.length" class="bz-fav-chips">
      <span class="bz-fav-label">
        <i class="material-icons" style="font-size:13px;line-height:1;">star</i>Favorites
      </span>
      <button v-for="f in favsOpen ? favEntries : favEntries.slice(0, 4)" :key="f.code"
        class="bz-fav-chip" type="button" @click="selectNode(f, 'favorite')">
        {{ f.name }}<code>{{ f.code }}</code>
      </button>
      <button v-if="favEntries.length > 4" class="bz-fav-chip more" type="button" @click="favsOpen = !favsOpen">
        {{ favsOpen ? 'Show less' : '+' + (favEntries.length - 4) + ' more' }}
      </button>
    </div>

    <!-- search -->
    <div class="bz-search">
      <i class="material-icons" style="font-size:18px;line-height:1;color:var(--bzc-faint);">search</i>
      <input v-model="q" placeholder="Search by keyword or bznaics code…" aria-label="Search industries" />
      <button v-if="q" class="bz-clear" type="button" @click="q = ''">
        <i class="material-icons" style="font-size:15px;line-height:1;">close</i>
      </button>
    </div>
    <div class="bz-search-modes">
      <span :class="{ on: q && !isCodeQuery }">Keyword</span>
      <span :class="{ on: isCodeQuery }">NAICS code</span>
      <span :class="{ on: !q }">Browse all sectors</span>
    </div>

    <!-- results / tree -->
    <div class="bz-picker-list" role="listbox">
      <template v-if="q.trim()">
        <div v-if="searching" class="bz-empty">Searching…</div>
        <template v-else-if="results.length">
          <div v-for="r in results" :key="'s' + r.code" class="bz-row"
            :class="{ sel: selected && selected.code === r.code }" @click="selectNode(r, isCodeQuery ? 'naics_code' : 'keyword')">
            <div class="bz-row-main">
              <span class="bz-row-name">
                {{ r.name }}<code class="bz-code">{{ r.code }}</code>
                <remap-flag :node="r" />
              </span>
            </div>
            <span v-if="showClassCounts && r.nClasses !== null" class="bz-avail-chip" :class="{ warn: chipWarn(r.nClasses) }">{{ r.nClasses }}/15 classes</span>
            <button class="bz-star" :class="{ on: favorites.includes(r.code) }" type="button" title="Favorite"
              @click.stop="$emit('toggle-fav', r.code)">
              <i class="material-icons" style="font-size:16px;line-height:1;">{{ favorites.includes(r.code) ? 'star' : 'star_outline' }}</i>
            </button>
          </div>
        </template>
        <div v-else class="bz-empty">No industries match “{{ q }}”. Try a broader keyword or a 2–6 digit code.</div>
      </template>

      <template v-else-if="loadingRoots">
        <div class="bz-empty">Loading sectors…</div>
      </template>
      <template v-else>
        <template v-for="row in flatTree">
          <div v-if="row.type === 'node'" :key="'n' + row.node.code" class="bz-row"
            :class="{ sel: selected && selected.code === row.node.code }"
            :style="{ paddingLeft: 10 + row.depth * 16 + 'px' }"
            @click="row.expandable ? toggleExpand(row.node) : selectNode(row.node, 'browse', row.path)">
            <button class="bz-caret" :class="{ none: !row.expandable }" type="button"
              @click.stop="row.expandable && toggleExpand(row.node)">
              <i v-if="row.expandable" class="material-icons" style="font-size:17px;line-height:1;">
                {{ expanded[row.node.code] ? 'expand_more' : 'chevron_right' }}
              </i>
            </button>
            <div class="bz-row-main">
              <span class="bz-row-name">
                {{ row.node.name }}<code class="bz-code">{{ row.node.code }}</code>
                <remap-flag :node="row.node" />
              </span>
            </div>
            <span v-if="showClassCounts && row.node.nClasses !== null" class="bz-avail-chip" :class="{ warn: chipWarn(row.node.nClasses) }">{{ row.node.nClasses }}/15 classes</span>
            <button v-if="row.expandable" class="bz-use" type="button"
              @click.stop="selectNode(row.node, 'browse', row.path)">Select</button>
            <button class="bz-star" :class="{ on: favorites.includes(row.node.code) }" type="button" title="Favorite"
              @click.stop="$emit('toggle-fav', row.node.code)">
              <i class="material-icons" style="font-size:16px;line-height:1;">{{ favorites.includes(row.node.code) ? 'star' : 'star_outline' }}</i>
            </button>
          </div>
          <div v-else-if="row.type === 'loading'" :key="'l' + row.parent" class="bz-deadend"
            :style="{ paddingLeft: 26 + row.depth * 16 + 'px' }">Loading…</div>
          <div v-else :key="'d' + row.parent" class="bz-deadend"
            :style="{ paddingLeft: 26 + row.depth * 16 + 'px' }">No further breakdown available for this code.</div>
        </template>
      </template>
      <!-- flat DOM keeps expansion state stable across page re-renders (handoff requirement) -->
    </div>
  </div>
</template>

<script>
import { fetchChildren, searchIndustries } from './model';
import RemapFlag from './RemapFlag.vue';

export default {
  name: 'industry-step',
  components: { RemapFlag },
  props: {
    product: { type: String, required: true }, // 'IFP' | 'IMP'
    selected: { type: Object, default: null },
    favorites: { type: Array, default: () => [] },
    // show the offered-sales-class count chip per row (IFP-family products)
    showClassCounts: { type: Boolean, default: false },
  },
  data() {
    return {
      q: '',
      results: [],
      searching: false,
      searchTimer: null,
      roots: [],
      loadingRoots: false,
      kids: {}, // code -> array of child nodes (loaded lazily)
      loading: {}, // code -> true while children are being fetched
      expanded: {}, // code -> bool
      favNodes: {}, // code -> resolved node (for chip labels)
      favsOpen: false,
    };
  },
  computed: {
    apiType() {
      return this.product === 'IMP' ? 'imr' : 'if';
    },
    isCodeQuery() {
      const t = this.q.trim();
      return t !== '' && /^[\d.\-]+$/.test(t);
    },
    favEntries() {
      return this.favorites
        .map((c) => this.favNodes[c] || { code: c, name: c, state: 'normal', msg: '' })
        .filter(Boolean);
    },
    // Flattened render list — walks roots + expanded/loaded children. Keeps the
    // DOM flat (no recursive components) and expansion state survives re-renders.
    flatTree() {
      const out = [];
      const walk = (nodes, depth, path) => {
        nodes.forEach((n) => {
          const loaded = this.kids[n.code];
          const isLoading = this.loading[n.code];
          // every node is expandable until the API proves it's a leaf
          const expandable = !loaded || loaded.length > 0;
          out.push({ type: 'node', node: n, depth, expandable, path });
          if (this.expanded[n.code]) {
            if (isLoading) out.push({ type: 'loading', parent: n.code, depth: depth + 1 });
            else if (loaded && loaded.length) walk(loaded, depth + 1, path.concat([n]));
            else if (loaded && !loaded.length) out.push({ type: 'deadend', parent: n.code, depth: depth + 1 });
          }
        });
      };
      walk(this.roots, 0, []);
      return out;
    },
  },
  watch: {
    q() {
      clearTimeout(this.searchTimer);
      const term = this.q.trim();
      if (!term) {
        this.results = [];
        this.searching = false;
        return;
      }
      this.searching = true;
      this.searchTimer = setTimeout(this.runSearch, 350);
    },
    favorites: {
      immediate: true,
      handler() {
        this.resolveFavNames();
      },
    },
  },
  async created() {
    this.loadingRoots = true;
    try {
      this.roots = await fetchChildren(this.apiType, '0');
    } catch (e) {
      this.roots = [];
    } finally {
      this.loadingRoots = false;
    }
  },
  methods: {
    chipWarn(n) {
      return n <= 4;
    },
    async runSearch() {
      const term = this.q.trim();
      try {
        const rows = await searchIndustries(this.apiType, term);
        if (this.q.trim() === term) {
          this.results = rows;
          this.searching = false;
        }
      } catch (e) {
        if (this.q.trim() === term) {
          this.results = [];
          this.searching = false;
        }
      }
    },
    async toggleExpand(node) {
      const code = node.code;
      this.$set(this.expanded, code, !this.expanded[code]);
      if (this.expanded[code] && !this.kids[code] && !this.loading[code]) {
        this.$set(this.loading, code, true);
        try {
          const rows = await fetchChildren(this.apiType, code);
          this.$set(this.kids, code, rows);
        } catch (e) {
          this.$set(this.kids, code, []);
        } finally {
          this.$set(this.loading, code, false);
        }
      }
    },
    selectNode(node, method, path) {
      this.$emit('select', { node, method, path: path || [] });
    },
    // Favorites are stored as codes; resolve display names from whatever nodes
    // we've already seen, falling back to a targeted search by code.
    async resolveFavNames() {
      const known = {};
      const collect = (nodes) => nodes.forEach((n) => { known[n.code] = n; });
      collect(this.roots);
      Object.keys(this.kids).forEach((k) => collect(this.kids[k]));
      let missing = [];
      this.favorites.forEach((c) => {
        if (known[c]) this.$set(this.favNodes, c, known[c]);
        else if (!this.favNodes[c]) missing.push(c);
      });
      for (const code of missing.slice(0, 8)) {
        try {
          const rows = await searchIndustries(this.apiType, code);
          const hit = rows.find((r) => r.code === code);
          if (hit) this.$set(this.favNodes, code, hit);
        } catch (e) {
          /* leave code-only chip */
        }
      }
    },
  },
};
</script>
